/**
 * Accommodation BIK Tab
 * 
 * Displays dormitory BIK calculations for IRAS Appendix 8A section d2
 * (Place of Residence).
 * 
 * Policy: All dormitory residents are taxable on accommodation BIK regardless
 * of pass type (Work Permit holders are NOT exempt). The taxable value is the
 * prorated rental the company pays for the resident's bed, sourced from the
 * Dorm Portal API and summed across the tax year. (Company-owned dormitory
 * valuation, e.g. 18 Woodlands, is handled in a later phase.)
 * 
 * Design System: Admin Theme (Red)
 */

import React, { useState, useMemo } from 'react';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import {
  Home,
  Users,
  DollarSign,
  RefreshCw,
  Info,
  Building2,
  User,
  Eye,
} from 'lucide-react';
import { useAccommodationBIKSummary } from '../../../../hooks/useBIKData';
import { formatDate } from '../../../../utils/dateUtils';
import IconButton from '../../../../components/UI/IconButton';
import { FilterButton } from '../../../../components/UI/FilterButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import AccommodationDetailModal from './AccommodationDetailModal';

const AccommodationBIKTab = ({ year }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPassType, setFilterPassType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [detailStaffId, setDetailStaffId] = useState(null);
  
  // Fetch accommodation BIK summary
  const { data: summary, isLoading, refetch } = useAccommodationBIKSummary(year);
  
  // Process data for display
  const employees = useMemo(() => {
    if (!summary?.employees) return [];
    
    return summary.employees.filter((emp) => {
      const matchesSearch = !searchTerm ||
        emp.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPassType = !filterPassType || emp.pass_type === filterPassType;
      
      return matchesSearch && matchesPassType;
    });
  }, [summary, searchTerm, filterPassType]);
  
  // Calculate totals
  const totals = useMemo(() => {
    return {
      totalEmployees: employees.length,
      taxableCount: employees.filter(e => parseFloat(e.taxable_value || 0) > 0).length,
      totalValue: employees.reduce((sum, e) => sum + parseFloat(e.taxable_value || 0), 0),
    };
  }, [employees]);
  
  const formatMoney = (value) =>
    `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;
  
  // Shared badges (used by both the desktop table and mobile cards)
  const renderPassTypeBadge = (emp) => (
    <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
      emp.pass_type === 'worker' 
        ? 'bg-green-100 text-green-800'
        : emp.pass_type === 'S-Pass'
        ? 'bg-blue-100 text-blue-800'
        : emp.pass_type === 'EP'
        ? 'bg-purple-100 text-purple-800'
        : 'bg-gray-100 text-gray-800'
    }`}>
      {emp.pass_type || 'Unknown'}
    </span>
  );
  
  const renderStatusBadge = () => (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
      <DollarSign className="w-3 h-3" />
      Taxable
    </span>
  );
  
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Accommodation BIK ({year})
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Dormitory benefits (IRAS Appendix 8A - d2)
            </p>
          </div>
          
          <div className="flex-shrink-0">
            <IconButton
              icon={RefreshCw}
              onClick={() => refetch()}
              title="Refresh"
              ariaLabel="Refresh accommodation BIK data"
              variant="default"
            />
          </div>
        </div>
      </div>
      
      {/* Policy Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-800">
              Dormitory Accommodation is Taxable
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              All dormitory residents are taxable on accommodation BIK regardless of
              pass type &mdash; Work Permit holders are NOT exempt. The taxable value is
              the rental the company pays for the resident's bed, sourced from the Dorm
              Portal. (Company-owned dormitory valuation is handled in a later phase.)
            </p>
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{totals.totalEmployees}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Dorm Residents</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{totals.taxableCount}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Taxable</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                ${totals.totalValue.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total d2 Value</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by employee..."
            className="max-w-xl"
          />

          {/* Filter button */}
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={filterPassType ? 1 : 0}
            onClick={() => setShowFilters(v => !v)}
          />
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label-admin">Pass Type</label>
                <select
                  value={filterPassType}
                  onChange={(e) => setFilterPassType(e.target.value)}
                  className="select-admin w-full"
                >
                  <option value="">All Pass Types</option>
                  <option value="worker">Work Permit</option>
                  <option value="S-Pass">S-Pass</option>
                  <option value="EP">Employment Pass</option>
                  <option value="Local Staff">Local Staff</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Employees List */}
      {employees.length === 0 ? (
        /* Shared empty state */
        <div className="bg-white rounded-2xl shadow-sm border px-6 py-12 text-center text-gray-500">
          <Home className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No accommodation records found for {year}</p>
        </div>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {employees.map((emp, index) => (
              <div
                key={emp.staff_id || index}
                className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer active:bg-gray-50 flex flex-col h-full"
                onClick={() => emp.staff_id && setDetailStaffId(emp.staff_id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {emp.employee_name || emp.staff_id}
                    </p>
                    <p className="text-xs text-gray-500">{emp.staff_id}</p>
                  </div>
                  <div className="flex-shrink-0">{renderStatusBadge(emp)}</div>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Pass Type</span>
                    <span className="flex-shrink-0">{renderPassTypeBadge(emp)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Dormitory</span>
                    <span className="text-gray-900 text-right truncate min-w-0">
                      {emp.dormitory_name || '-'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Period</span>
                    <span className="text-gray-900 text-right min-w-0">
                      {emp.months_occupied || 12} months
                      {emp.start_date && emp.end_date && (
                        <span className="block text-xs text-gray-500 truncate">
                          {formatDate(emp.start_date)} <span className="text-gray-400 mx-0.5">&rarr;</span> {formatDate(emp.end_date)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">d2 Value</span>
                    <span className="text-right truncate min-w-0 tabular-nums font-semibold text-gray-900">
                      {formatMoney(emp.taxable_value)}
                    </span>
                  </div>
                </div>
                <div className="card-action-footer">
                  <button
                    onClick={(e) => { e.stopPropagation(); emp.staff_id && setDetailStaffId(emp.staff_id); }}
                    className="icon-button-admin"
                    title="View BIK breakdown"
                    aria-label={`View accommodation BIK breakdown for ${emp.employee_name || emp.staff_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop: full table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Employee
                    </th>
                    <th className="table-header-cell">
                      Pass Type
                    </th>
                    <th className="table-header-cell">
                      Dormitory
                    </th>
                    <th className="table-header-cell">
                      Period
                    </th>
                    <th className="table-header-cell text-center">
                      Status
                    </th>
                    <th className="table-header-cell text-right">
                      d2 Value
                    </th>
                    <th className="table-header-cell text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((emp, index) => (
                    <tr
                      key={emp.staff_id || index}
                      className="table-row-dense cursor-pointer hover:bg-gray-50"
                      onClick={() => emp.staff_id && setDetailStaffId(emp.staff_id)}
                      title="View BIK breakdown"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {emp.employee_name || emp.staff_id}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{emp.staff_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {renderPassTypeBadge(emp)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {emp.dormitory_name || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{emp.months_occupied || 12} months</p>
                        {emp.start_date && emp.end_date && (
                          <p className="text-xs text-gray-500">
                            {formatDate(emp.start_date)} <span className="text-gray-400 mx-0.5">&rarr;</span> {formatDate(emp.end_date)}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {renderStatusBadge(emp)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          {formatMoney(emp.taxable_value)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); emp.staff_id && setDetailStaffId(emp.staff_id); }}
                          className="icon-button-admin"
                          title="View BIK breakdown"
                          aria-label={`View accommodation BIK breakdown for ${emp.employee_name || emp.staff_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      
      {/* Calculation Method Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-gray-600" />
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-2">Calculation Method:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Applies to all dormitory residents regardless of pass type (no Work Permit exemption)</li>
              <li>Taxable value = the prorated rental the company pays for the resident's bed, summed across the year</li>
              <li>Residency, occupancy days and rental cost are sourced from the Dorm Portal</li>
              <li>Company-owned dormitory valuation (e.g. 18 Woodlands Industrial Park E1) will be added in a later phase</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Per-worker BIK breakdown */}
      <AccommodationDetailModal
        isOpen={detailStaffId != null}
        staffId={detailStaffId}
        year={year}
        onClose={() => setDetailStaffId(null)}
      />
    </div>
  );
};

export default AccommodationBIKTab;
